import React, { useState } from 'react';
import type {
  ControlTowerProps,
  Agent,
  ContainmentStatus as ContainmentStatusType,
  BondSummary,
  SlashingEvent,
  Reputation,
  VerticalSummary,
  Attestation,
  QuarantinedAgent,
  AppealHistory,
  MetabolicSnapshot,
} from '../types';
import { ContainmentStatus } from './ContainmentStatus';
import { BondManager } from './BondManager';
import { AgentFleet } from './AgentFleet';
import { HopsMarketplace } from './HopsMarketplace';
import { QuarantineZone } from './QuarantineZone';
import { useSymsvoxData, useMockSymsvoxData } from '../hooks/useSymsvoxData';
import type { SYMSVOXActions } from '../hooks/useSymsvoxData';

export interface ControlTowerData {
  containment: ContainmentStatusType;
  healthHistory: MetabolicSnapshot[];
  bonds: BondSummary;
  slashingEvents: SlashingEvent[];
  hopsEarnings: number;
  agents: Agent[];
  reputation: Reputation;
  verticals: VerticalSummary[];
  attestations: Attestation[];
  quarantined: QuarantinedAgent[];
  appealHistory: AppealHistory[];
}

export interface ControlTowerFullProps extends ControlTowerProps {
  data: ControlTowerData;
  onSpawnAgent?: () => void;
  onDepositBond?: () => void;
  onWithdrawBond?: () => void;
  onFileAppeal?: (agentDid: string) => void;
  onPauseAgent?: (agent: Agent) => void;
  onTerminateAgent?: (agent: Agent) => void;
  onUpgradeAgent?: (agent: Agent) => void;
  onViewAgentLogs?: (agent: Agent) => void;
}

type Tab = 'overview' | 'agents' | 'marketplace' | 'quarantine';

/**
 * Main Control Tower Dashboard
 * The "BSL-4 Control Center" for all SYMSVOX operations
 */
export function ControlTower({
  operatorDid,
  theme = 'dark',
  data,
  onAgentSelect,
  onSpawnAgent,
  onDepositBond,
  onWithdrawBond,
  onFileAppeal,
  onPauseAgent,
  onTerminateAgent,
  onUpgradeAgent,
  onViewAgentLogs,
  onError,
}: ControlTowerFullProps) {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const isDark = theme === 'dark';

  const tabs: { id: Tab; label: string; badge?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'agents', label: 'Agents', badge: data.agents.length },
    { id: 'marketplace', label: 'Marketplace' },
    {
      id: 'quarantine',
      label: 'Quarantine',
      badge: data.quarantined.length > 0 ? data.quarantined.length : undefined,
    },
  ];

  return (
    <div
      className={`min-h-screen ${
        isDark ? 'bg-gray-950 text-white' : 'bg-gray-50 text-gray-900'
      }`}
    >
      {/* Header */}
      <header
        className={`border-b ${isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo & Title */}
            <div className="flex items-center gap-3">
              <span className="text-2xl">🦀</span>
              <div>
                <h1 className="text-xl font-bold">SYMSVOX Control Tower</h1>
                <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  BSL-4 Agent Containment Dashboard
                </p>
              </div>
            </div>

            {/* Operator Info */}
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Operator
                </div>
                <code className={`text-sm font-mono ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {operatorDid.slice(0, 12)}...{operatorDid.slice(-6)}
                </code>
              </div>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isDark ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                }`}
              >
                L{data.reputation.level}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <nav className="flex gap-1 mt-4 -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === tab.id
                    ? isDark
                      ? 'bg-gray-800 text-white border-t border-x border-gray-700'
                      : 'bg-white text-gray-900 border-t border-x border-gray-200'
                    : isDark
                    ? 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {tab.label}
                {tab.badge !== undefined && (
                  <span
                    className={`ml-2 px-1.5 py-0.5 text-xs rounded ${
                      tab.id === 'quarantine' && tab.badge > 0
                        ? 'bg-red-500 text-white'
                        : isDark
                        ? 'bg-gray-700 text-gray-300'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ContainmentStatus
              status={data.containment}
              healthHistory={data.healthHistory}
            />
            <BondManager
              bonds={data.bonds}
              slashingEvents={data.slashingEvents}
              hopsEarnings={data.hopsEarnings}
              onDeposit={onDepositBond}
              onWithdraw={onWithdrawBond}
            />
            <div className="lg:col-span-2">
              <AgentFleet
                agents={data.agents.slice(0, 3)}
                onSpawn={onSpawnAgent}
                onSelect={onAgentSelect}
                onPause={onPauseAgent}
                onTerminate={onTerminateAgent}
                onUpgrade={onUpgradeAgent}
                onViewLogs={onViewAgentLogs}
              />
            </div>
          </div>
        )}

        {activeTab === 'agents' && (
          <AgentFleet
            agents={data.agents}
            onSpawn={onSpawnAgent}
            onSelect={onAgentSelect}
            onPause={onPauseAgent}
            onTerminate={onTerminateAgent}
            onUpgrade={onUpgradeAgent}
            onViewLogs={onViewAgentLogs}
          />
        )}

        {activeTab === 'marketplace' && (
          <HopsMarketplace
            reputation={data.reputation}
            verticals={data.verticals}
            attestations={data.attestations}
          />
        )}

        {activeTab === 'quarantine' && (
          <QuarantineZone
            quarantined={data.quarantined}
            appealHistory={data.appealHistory}
            onFileAppeal={onFileAppeal}
          />
        )}
      </main>

      {/* Footer */}
      <footer
        className={`border-t mt-auto ${
          isDark ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            SYMSVOX Protocol v1.0.0 • BSL-4 Containment
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://docs.symsvox.ai"
              className={`text-sm ${
                isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
              } transition-colors`}
            >
              Documentation
            </a>
            <a
              href="https://status.symsvox.ai"
              className={`text-sm ${
                isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
              } transition-colors`}
            >
              Status
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// =============================================================================
// Connected Control Tower (with API integration)
// =============================================================================

export interface ConnectedControlTowerProps extends ControlTowerProps {
  /** Use mock data instead of API (for development) */
  useMockData?: boolean;
  /** Custom refresh interval in ms (default: 30000) */
  refreshInterval?: number;
}

/**
 * Connected Control Tower with API integration
 * Automatically fetches data and wires up actions
 *
 * @example
 * ```tsx
 * <ConnectedControlTower
 *   operatorDid="did:sym:op-123"
 *   apiUrl="http://localhost:3001"
 * />
 * ```
 */
export function ConnectedControlTower({
  operatorDid,
  apiKey,
  apiUrl = 'http://localhost:3001',
  theme = 'dark',
  useMockData = false,
  refreshInterval = 30000,
  onAgentSelect,
  onError,
}: ConnectedControlTowerProps) {
  // Use mock data or real API
  const mockData = useMockSymsvoxData();
  const { data, loading, error, actions } = useSymsvoxData({
    operatorDid,
    apiKey,
    apiUrl,
    refreshInterval,
  });

  // Report errors
  React.useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  // Use mock data in development
  const displayData = useMockData ? mockData : data;

  if (loading && !useMockData) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-950' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="text-4xl mb-4">🦀</div>
          <div className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Loading Control Tower...
          </div>
        </div>
      </div>
    );
  }

  if (error && !useMockData) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme === 'dark' ? 'bg-gray-950' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <div className={`text-lg ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
            Failed to connect to SYMSVOX API
          </div>
          <div className={`text-sm mt-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
            {error.message}
          </div>
        </div>
      </div>
    );
  }

  if (!displayData) {
    return null;
  }

  return (
    <ControlTower
      operatorDid={operatorDid}
      theme={theme}
      data={displayData}
      onAgentSelect={onAgentSelect}
      onPauseAgent={(agent) => actions.pauseAgent(agent.did)}
      onTerminateAgent={(agent) => actions.terminateAgent(agent.did)}
      onUpgradeAgent={(agent) => actions.stakeAgent(agent.did, 500)}
      onError={onError}
    />
  );
}
